// BracketGrid: 7-column horizontal NBA playoff bracket layout.
// Renders West R1 → West R2 → West CF → Finals ← East CF ← East R2 ← East R1
// with connector lines between columns. Mobile (<768px): vertical flow via BracketMobileVertical.
// Desktop: CSS transform: scale() auto-fits bracket to available viewport width.

import { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BracketSlot } from '@/hooks/use-bracket';
import { BracketPick, BracketPicks } from '@/types/bracket';
import BracketMatchupSlot from '@/components/BracketMatchupSlot';
import BracketConnectors from '@/components/BracketConnectors';
import ScorePickerSheet from '@/components/ScorePickerSheet';
import BracketMobileVertical from '@/components/BracketMobileVertical';

interface SeriesOddsRow {
  id: string;
  series_id: string | null;
  team: string;
  outcome: string;
  team_wins: number;
  opponent_wins: number;
  american_odds: number;
  decimal_odds: number | null;
  multiplier: number | null;
}

interface BracketGridProps {
  bracketSlots: BracketSlot[];
  picks: BracketPicks;
  oddsMap: Record<string, SeriesOddsRow[]>;
  onPickWinner: (slotId: string, winner: string) => void;
  onPickScore: (slotId: string, score: string, americanOdds: number | null, winner: string) => void;
  onClearPick: (slotId: string) => void;
  isLocked: boolean;
  isViewingOther: boolean;
}

const COLUMN_LABELS = [
  'סיבוב 1 – מערב',
  'סיבוב 2',
  'גמר מערבי',
  'הגמר',
  'גמר מזרחי',
  'סיבוב 2',
  'סיבוב 1 – מזרח',
];

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.05 },
  },
};

const BracketGrid = ({
  bracketSlots,
  picks,
  oddsMap,
  onPickWinner,
  onPickScore,
  onClearPick,
  isLocked,
  isViewingOther,
}: BracketGridProps) => {
  // ── Refs for BracketScaler ────────────────────────────────────────────────
  const containerRef = useRef<HTMLDivElement>(null);
  const bracketRef = useRef<HTMLDivElement>(null);

  // Responsive breakpoint: switch to vertical layout below 768px
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check(); // sync on mount in case initial useState was wrong
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ── BracketScaler: CSS transform scale to fit any viewport ────────────────
  // Measures actual bracket scrollWidth vs available container width.
  // Scale down if bracket is wider than container; never scale up.
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (isMobile) {
      setScale(1);
      return;
    }

    const updateScale = () => {
      if (!containerRef.current || !bracketRef.current) return;
      const availableWidth = containerRef.current.clientWidth;
      const bracketWidth = bracketRef.current.scrollWidth;
      // Floor at 0.3 — never shrink below 30% even on narrow viewports
      const newScale = Math.max(0.3, Math.min(1, availableWidth / bracketWidth));
      setScale(newScale);
    };

    // Initial measurement after layout
    updateScale();
    // Re-measure on resize
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [isMobile]);

  // Active slot for the score picker popup
  const [activeScoreSlot, setActiveScoreSlot] = useState<string | null>(null);

  const handleTapForScore = (slotId: string) => setActiveScoreSlot(slotId);

  // Derive active slot data for the picker
  const activeSlot = activeScoreSlot
    ? bracketSlots.find(s => s.slotId === activeScoreSlot) ?? null
    : null;
  const activeOdds = activeScoreSlot ? (oddsMap[activeScoreSlot] ?? []) : [];

  // ── Column slot groups ──────────────────────────────────────────────────

  const westR1 = bracketSlots
    .filter(s => s.conference === 'west' && (s.round === 'round1' || s.round === 'first_round'))
    .sort((a, b) => a.position - b.position);

  const westR2 = bracketSlots
    .filter(s => s.round === 'west_semis')
    .sort((a, b) => a.position - b.position);

  const westCF = bracketSlots.filter(s => s.round === 'west_finals');

  const finals = bracketSlots.filter(s => s.round === 'finals');

  const eastCF = bracketSlots.filter(s => s.round === 'east_finals');

  const eastR2 = bracketSlots
    .filter(s => s.round === 'east_semis')
    .sort((a, b) => a.position - b.position);

  const eastR1 = bracketSlots
    .filter(s => s.conference === 'east' && (s.round === 'round1' || s.round === 'first_round'))
    .sort((a, b) => a.position - b.position);

  // ── Connector fill helpers ──────────────────────────────────────────────

  // Check if a slot has a winner picked that flows to a downstream slot
  const hasPick = (slotId: string) => !!picks[slotId]?.winner;

  // West R1→R2 connector pairs: pair[0] = slots 0+1 → r2_west_0, pair[1] = slots 2+3 → r2_west_1
  const westR1toR2Pairs = [
    { topFilled: hasPick(westR1[0]?.slotId), bottomFilled: hasPick(westR1[1]?.slotId) },
    { topFilled: hasPick(westR1[2]?.slotId), bottomFilled: hasPick(westR1[3]?.slotId) },
  ];

  const westR2toCFPairs = [
    { topFilled: hasPick(westR2[0]?.slotId), bottomFilled: hasPick(westR2[1]?.slotId) },
  ];

  const westCFtoFinalsPairs = [
    { topFilled: hasPick(westCF[0]?.slotId), bottomFilled: false },
  ];

  const eastCFtoFinalsPairs = [
    { topFilled: hasPick(eastCF[0]?.slotId), bottomFilled: false },
  ];

  const eastR2toCFPairs = [
    { topFilled: hasPick(eastR2[0]?.slotId), bottomFilled: hasPick(eastR2[1]?.slotId) },
  ];

  const eastR1toR2Pairs = [
    { topFilled: hasPick(eastR1[0]?.slotId), bottomFilled: hasPick(eastR1[1]?.slotId) },
    { topFilled: hasPick(eastR1[2]?.slotId), bottomFilled: hasPick(eastR1[3]?.slotId) },
  ];

  // ── Check if a slot has downstream picks (for clear confirmation) ─────
  const hasDownstreamPicks = (() => {
    if (!activeSlot) return false;
    const winner = picks[activeSlot.slotId]?.winner;
    if (!winner) return false;
    // Check if any downstream slot's pick depends on this winner
    const checkDownstream = (slot: BracketSlot): boolean => {
      if (!slot.feedsInto) return false;
      const downstreamSlot = bracketSlots.find(s => s.slotId === slot.feedsInto);
      if (!downstreamSlot) return false;
      const downstreamPick = picks[downstreamSlot.slotId];
      if (downstreamPick) return true;
      return checkDownstream(downstreamSlot);
    };
    return checkDownstream(activeSlot);
  })();

  // ── Score picker (single instance, always mounted) ─────────────────────

  const scorePickerElement = (
    <ScorePickerSheet
      isOpen={!!activeScoreSlot}
      onClose={() => setActiveScoreSlot(null)}
      slotId={activeSlot?.slotId ?? ''}
      round={activeSlot?.round ?? ''}
      teamHome={activeSlot?.teamHome ?? null}
      teamAway={activeSlot?.teamAway ?? null}
      pick={activeScoreSlot ? picks[activeScoreSlot] : undefined}
      odds={activeOdds}
      isLocked={isLocked}
      isViewingOther={isViewingOther}
      hasDownstreamPicks={hasDownstreamPicks}
      onPickScore={(slotId, score, odds, winner) => {
        onPickScore(slotId, score, odds, winner);
        setActiveScoreSlot(null);
      }}
      onClearPick={(slotId) => {
        onClearPick(slotId);
        setActiveScoreSlot(null);
      }}
      onPickWinnerOnly={(slotId, winner) => {
        onPickWinner(slotId, winner);
        setActiveScoreSlot(null);
      }}
    />
  );

  // ── Column renderer helpers ─────────────────────────────────────────────

  const renderColumn = (
    slots: BracketSlot[],
    justifyClass: string,
    label: string,
    isFinals = false,
  ) => (
    <div className={`flex flex-col ${isFinals ? 'w-[180px]' : 'w-[160px]'}`}>
      {/* Column label */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-sm pb-2 mb-1">
        <p className="text-[10px] uppercase tracking-[0.2em] text-accent font-bold text-center whitespace-nowrap">
          {label}
        </p>
      </div>
      {/* Slots */}
      <div className={`flex flex-col gap-3 flex-1 ${justifyClass}`}>
        {slots.map(slot => (
          <BracketMatchupSlot
            key={slot.slotId}
            slotId={slot.slotId}
            round={slot.round}
            teamHome={slot.teamHome}
            teamAway={slot.teamAway}
            pick={picks[slot.slotId]}
            isLocked={isLocked}
            isViewingOther={isViewingOther}
            isFinals={isFinals}
            isPropagated={slot.fedBy !== null && (slot.teamHome !== null || slot.teamAway !== null)}
            onPickWinner={onPickWinner}
            onTapForScore={handleTapForScore}
          />
        ))}
      </div>
    </div>
  );

  // ── Single-slot connector (CF ↔ Finals): just a horizontal line ─────────
  const SingleLineConnector = ({ filled }: { filled: boolean }) => (
    <div className="flex items-center w-4 shrink-0 self-stretch">
      <div className={`h-[2px] w-full ${filled ? 'bg-accent shadow-[0_0_4px_rgba(234,88,12,0.5)]' : 'bg-border'}`} />
    </div>
  );

  return (
    <>
    {/* ── Mobile: vertical layout, NO scaler (CSS-enforced) ───────────── */}
    <div className="md:hidden">
      <BracketMobileVertical
        westR1={westR1}
        westR2={westR2}
        westCF={westCF}
        finals={finals}
        eastCF={eastCF}
        eastR2={eastR2}
        eastR1={eastR1}
        picks={picks}
        oddsMap={oddsMap}
        isLocked={isLocked}
        isViewingOther={isViewingOther}
        onPickWinner={onPickWinner}
        onTapForScore={handleTapForScore}
      />
    </div>

    {/* ── Desktop: horizontal layout with scaler (CSS-enforced) ───────── */}
    <div className="hidden md:block">
      <div ref={containerRef} style={{ width: '100%', overflow: 'hidden' }}>
        <div
          ref={bracketRef}
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
            ...(scale < 1 && bracketRef.current ? {
              height: bracketRef.current.scrollHeight * scale,
            } : {}),
          }}
        >
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="flex items-stretch gap-0 mx-auto w-max"
        >
          {renderColumn(westR1, 'justify-around', COLUMN_LABELS[0])}
          <BracketConnectors direction="converge-left" pairs={westR1toR2Pairs} />
          {renderColumn(westR2, 'justify-around', COLUMN_LABELS[1])}
          <BracketConnectors direction="converge-left" pairs={westR2toCFPairs} />
          {renderColumn(westCF, 'justify-center', COLUMN_LABELS[2])}
          <SingleLineConnector filled={hasPick(westCF[0]?.slotId)} />
          <div className="relative flex flex-col justify-center">
            <div
              className="pointer-events-none absolute inset-0 z-0"
              style={{
                background: 'radial-gradient(ellipse 120% 80% at 50% 50%, rgba(234,88,12,0.08) 0%, transparent 70%)',
              }}
            />
            <div className="relative z-[1]">
              {renderColumn(finals, 'justify-center', COLUMN_LABELS[3], true)}
            </div>
          </div>
          <SingleLineConnector filled={hasPick(eastCF[0]?.slotId)} />
          {renderColumn(eastCF, 'justify-center', COLUMN_LABELS[4])}
          <BracketConnectors direction="converge-right" pairs={eastR2toCFPairs} />
          {renderColumn(eastR2, 'justify-around', COLUMN_LABELS[5])}
          <BracketConnectors direction="converge-right" pairs={eastR1toR2Pairs} />
          {renderColumn(eastR1, 'justify-around', COLUMN_LABELS[6])}
        </motion.div>
        </div>
      </div>
    </div>

    {scorePickerElement}
    </>
  );
};

export default BracketGrid;
